import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useForm, Controller } from 'react-hook-form';
import { CustomInput } from '../components/CustomInput';
import { DatePickerInput } from '../components/DatePickerInput';
import { AppModal, ModalType } from '../components/AppModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import ApiService from '../services/ApiService';
import { Employee } from '../models/types';
import { numericValidation, alphabetOnlyWithSpace } from '../utils/helpers';

interface EmployeeFormData {
    name: string;
    phone: string;
    fixedSalary: string;
    joiningDate: string;
    notes: string;
    isActive: boolean;
}

const AddEditEmployeeScreen = ({ navigation, route }: any) => {
    const existingEmployee = route.params?.employee as Employee | undefined;
    const isEditMode = !!existingEmployee;

    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        isSuccess?: boolean;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<EmployeeFormData>({
        defaultValues: {
            name: existingEmployee?.name || '',
            phone: existingEmployee?.phone || '',
            fixedSalary: existingEmployee?.fixedSalary?.toString() || '',
            joiningDate: existingEmployee?.joiningDate || new Date().toISOString(),
            notes: existingEmployee?.notes || '',
            isActive: existingEmployee?.isActive ?? true,
        },
        mode: 'onChange',
    });

    const isActive = watch('isActive');

    const handleModalClose = () => {
        const isSuccess = modal.isSuccess;
        setModal(prev => ({ ...prev, visible: false }));
        if (isSuccess) {
            navigation.goBack();
        }
    };

    const onSubmit = useCallback(async (data: EmployeeFormData) => {
        setLoading(true);
        try {
            const employeeData = {
                name: data.name.trim(),
                phone: data.phone.trim() || undefined,
                fixedSalary: Number(data.fixedSalary),
                joiningDate: data.joiningDate,
                notes: data.notes.trim() || undefined,
                isActive: data.isActive,
            };

            if (isEditMode && existingEmployee) {
                await ApiService.updateEmployee(existingEmployee.id, employeeData);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Employee updated successfully',
                    isSuccess: true,
                });
            } else {
                await ApiService.addEmployee(employeeData);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Employee added successfully',
                    isSuccess: true,
                });
            }
        } catch (error) {
            console.error('Error saving employee:', error);
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: 'Failed to save employee. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    }, [isEditMode, existingEmployee]);

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <ScreenHeader
                title={isEditMode ? 'Edit Employee' : 'Add Employee'}
                showBackButton={true}
                navigation={navigation}
            />

            <KeyboardAwareScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.contentContainer}
                showsVerticalScrollIndicator={false}
                bottomOffset={20}
            >
                <View style={styles.form}>
                    <Controller
                        control={control}
                        name="name"
                        rules={{
                            required: 'Employee name is required',
                            validate: (value) => value.trim().length > 0 || 'Name cannot be only spaces'
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Employee Name *"
                                value={value}
                                onChangeText={(text) => onChange(alphabetOnlyWithSpace(text))}
                                onBlur={onBlur}
                                icon="account"
                                error={errors.name?.message}
                                placeholder="e.g., Ramesh Kumar"
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="phone"
                        rules={{
                            required: 'Phone number is required',
                            minLength: { value: 9, message: 'Phone number must be at least 9 digits' },
                            maxLength: { value: 10, message: 'Phone number must not exceed 10 digits' }
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Phone Number *"
                                value={value}
                                onChangeText={(text) => onChange(numericValidation(text))}
                                onBlur={onBlur}
                                icon="phone"
                                keyboardType="number-pad"
                                error={errors.phone?.message}
                                placeholder="e.g., 9876543210"
                                maxLength={10}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="fixedSalary"
                        rules={{ required: 'Fixed salary is required' }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Fixed Salary (₹/month) *"
                                value={value}
                                onChangeText={(text) => onChange(numericValidation(text))}
                                onBlur={onBlur}
                                icon="currency-inr"
                                keyboardType="number-pad"
                                error={errors.fixedSalary?.message}
                                placeholder="e.g., 15000"
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="joiningDate"
                        rules={{ required: 'Joining date is required' }}
                        render={({ field: { onChange, value } }) => (
                            <DatePickerInput
                                label="Joining Date *"
                                value={value}
                                onChangeDate={onChange}
                                error={errors.joiningDate?.message}
                                maximumDate={new Date()}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="notes"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Notes (Optional)"
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                icon="note-text"
                                placeholder="Any additional information..."
                                multiline
                                numberOfLines={3}
                            />
                        )}
                    />

                    {/* Status Toggle (only for edit mode) */}
                    {isEditMode && (
                        <View style={styles.statusContainer}>
                            <Text style={styles.statusLabel}>Status</Text>
                            <View style={styles.statusToggle}>
                                <TouchableOpacity
                                    style={[
                                        styles.statusOption,
                                        isActive && styles.statusOptionActive,
                                    ]}
                                    onPress={() => setValue('isActive', true)}
                                >
                                    <Text style={[
                                        styles.statusOptionText,
                                        isActive && styles.statusOptionTextActive,
                                    ]}>Active</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={[
                                        styles.statusOption,
                                        !isActive && styles.statusOptionInactive,
                                    ]}
                                    onPress={() => setValue('isActive', false)}
                                >
                                    <Text style={[
                                        styles.statusOptionText,
                                        !isActive && styles.statusOptionTextInactive,
                                    ]}>Inactive</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    )}

                    {/* Action Buttons */}
                    <View style={styles.buttonRow}>
                        <TouchableOpacity
                            style={styles.cancelBtn}
                            onPress={() => navigation.goBack()}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.saveBtn}
                            onPress={handleSubmit(onSubmit)}
                            activeOpacity={0.7}
                            disabled={loading}
                        >
                            <Text style={styles.saveBtnText}>
                                {loading ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </KeyboardAwareScrollView>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={handleModalClose}
            />

            <LoadingOverlay visible={loading} message="Saving..." />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    scrollView: {
        flex: 1,
    },
    contentContainer: {
        flexGrow: 1,
    },
    form: {
        padding: theme.spacing.lg,
    },
    buttonRow: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginTop: theme.spacing.lg,
    },
    cancelBtn: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    cancelBtnText: {
        ...theme.typography.button,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
    saveBtn: {
        flex: 2,
        alignItems: 'center',
        justifyContent: 'center',
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.primary,
        ...theme.shadows.sm,
    },
    saveBtnText: {
        ...theme.typography.button,
        color: '#FFFFFF',
        fontWeight: '700',
    },
    statusContainer: {
        marginBottom: theme.spacing.md,
    },
    statusLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.sm,
        fontWeight: '600',
    },
    statusToggle: {
        flexDirection: 'row',
        gap: theme.spacing.sm,
    },
    statusOption: {
        flex: 1,
        paddingVertical: theme.spacing.md,
        borderRadius: theme.borderRadius.md,
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    statusOptionActive: {
        backgroundColor: '#E8F5E9',
        borderColor: '#4CAF50',
    },
    statusOptionInactive: {
        backgroundColor: '#FFEBEE',
        borderColor: '#F44336',
    },
    statusOptionText: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
    statusOptionTextActive: {
        color: '#4CAF50',
    },
    statusOptionTextInactive: {
        color: '#F44336',
    },
});

export default AddEditEmployeeScreen;
